/**
/**
 * UsersReviews Component
 *
 * Displays a list of user reviews for a course, with star ratings and review text.
 * Handles pagination of reviews and displays a button to load more reviews.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Renders user avatar, name, date, star rating, and review text
 * - Handles pagination and loading more reviews
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Array} props.reviewsList - Array of user review objects
 * @returns {JSX.Element} The rendered user reviews section
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import ReactStars from 'react-rating-stars-component';
import Button from '@mui/material/Button';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import classes from './UsersReviews.module.css';

const UsersReviews = props => {
  const [reviews, setReviews] = useState(5);

  /**
   * Handler to load more reviews
   * Passed as prop to Button and invoked on click
   */
  const loadReviewsHandler = () => {
    setReviews(reviews + 5);
  };

  return (
    <>
      {props.reviewsList.map(
        (userReview, index) =>
          index < reviews && (
            <div
              key={userReview.userName}
              className={classes.reviewContainer}
            >
              <div className={classes.topReview}>
                <figure className={classes.userReviewAvatar}>
                  <img
                    style={{ borderRadius: '50%' }}
                    className={classes.img} // Prop passed: image styling
                    src={userReview.userImage} // Prop passed: user avatar URL
                    alt={userReview.userName} // Prop passed: user name for alt text
                  />
                </figure>
                <aside className={classes.textContainer}>
                  <p className={classes.userName}>
                    {userReview.userName}
                    {/* Prop passed: user name */}
                  </p>
                  <p className={classes.dateText}>
                    {userReview.pastTime}
                    {/* Prop passed: review date */}
                  </p>
                </aside>
                <aside className={classes.userReviewStars}>
                  <ReactStars
                    edit={false}
                    count={5}
                    isHalf={true}
                    size={24}
                    activeColor="#e6a817"
                    emptyStarColor="#d6d6d6"
                    value={userReview.rated} // Prop passed: user rating value
                  />
                </aside>
              </div>
              <div>
                <ThumbUpOutlinedIcon
                  className={classes.userReviewIcon} // Prop passed: icon styling
                />
                <p className={classes.reviewText}>
                  {userReview.review}
                  {/* Prop passed: review text */}
                </p>
              </div>
            </div>
          )
      )}
      {reviews < props.reviewsList.length && (
        <Button
          onClick={loadReviewsHandler} // Prop passed: load more handler
          className={classes.showMoreReviews} // Prop passed: button styling
        >
          Ver más valoraciones
        </Button>
      )}
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
UsersReviews.propTypes = {
  /**
   * Array of user review objects
   * Passed as prop to render user reviews
   */
  reviewsList: PropTypes.array
};

UsersReviews.defaultProps = {
  reviewsList: []
};

export default UsersReviews;
